import React, { useContext, useState, useEffect } from 'react'
import styled, { useTheme } from 'styled-components'
import { Node, Transforms } from 'vendor/slate'
import { ReactEditor, useReadOnly } from 'vendor/slate-react'
import { EditorDispatch } from 'cg/components/documents/editor/slate/reducer'
import { RefractorAutoDetect } from 'cg/components/shared/refractor/refractorAutoDetect'
import copy from 'copy-to-clipboard'
import Select from 'react-select'
import { OverlayTrigger, Tooltip, Button } from 'react-bootstrap'
import { getSelectTheme } from 'Shared/theme'
import { tooltipOverlay } from 'cg/components/shared/tooltipOverlay'

const CodeTabsContentItem = ({ refractor, attributes, children, element, editor }) => {
  const { state, dispatch } = useContext(EditorDispatch) || {}
  const { activeTabs } = state || {}
  const readOnly = useReadOnly()
  const { tabId, language, noTabs } = element

  const path = ReactEditor.findPath(editor, element)
  const codeTabs = Node.has(editor, path) ? Node.parent(editor, path) : null
  const [copied, setCopied] = useState(false)
  const supportedModes = new RefractorAutoDetect().getSupportedModes()
  const options = []

  useEffect(() => {
    if (language === 'plaintext') return
    if (language && !refractor?.registered(language)) {
      try {
        refractor.register(require(`refractor/lang/${language}.js`))
      } catch (err) {}
    }
  }, [language, refractor])

  if (!readOnly) {
    options.push({ label: 'plaintext', value: 'plaintext' })
    Object.keys(supportedModes).forEach(language => {
      options.push({ label: language, value: language })
    })
  }

  const selectedLanguage = supportedModes[language] ? language : 'plaintext'
  const copyText = copied ? 'Copied!' : 'Copy'

  const onChange = option => {
    const { value } = option
    Transforms.setNodes(editor, { language: value }, { at: path })
  }

  const copyCode = () => {
    let text = ''
    element.children.forEach(child => {
      for (const [node] of Node.texts(child)) {
        text += node.text
      }
      text += '\n'
    })
    setCopied(true)
    copy(text)
  }

  const isTabActive = activeTabs
    ? codeTabs
      ? activeTabs[codeTabs.id] === tabId
      : false
    : codeTabs
      ? codeTabs.defaultActiveTab === tabId
      : false
  const theme = useTheme()

  return (
    <CodeTabContentItem {...attributes} active={isTabActive} $noTabs={noTabs || false}>
      <CodeTabContentItemHeader contentEditable={false}>
        <Select
          styles={SelectStyles({ $readOnly: readOnly, theme })}
          menuPlacement="auto"
          theme={props => ({
            ...props,
            colors: {
              ...props.colors,
              ...getSelectTheme(theme.id)
            },
            borderRadius: 3,
            spacing: {
              controlHeight: 25,
              baseUnit: 1,
              menuGutter: 6
            }
          })}
          getOptionLabel={option => option.label}
          getOptionValue={option => option.value}
          isDisabled={readOnly}
          value={{ label: selectedLanguage, value: selectedLanguage }}
          onChange={onChange}
          options={options}
        />
        <ClipboardButton
          onClick={() => {
            copyCode()
          }}
        >
          <OverlayTrigger
            placement="bottom"
            overlay={overlayProps => tooltipOverlay(<Tooltip id="tooltip">{copyText}</Tooltip>, overlayProps)}
          >
            <Button variant="tertiary" size="sm">
              <i className="fad fa-copy" />
            </Button>
          </OverlayTrigger>
        </ClipboardButton>
      </CodeTabContentItemHeader>
      <PRE key={language} spellCheck="false">
        {children}
      </PRE>
    </CodeTabContentItem>
  )
}

export default CodeTabsContentItem

const ClipboardButton = styled.div``
const CodeTabContentItem = styled.div`
  display: ${props => (props.active ? 'block' : 'none')};
  background: ${props => props.theme.bgSecondLightest};
  border: 1px solid ${props => props.theme.borderColor};
  border-top: ${props => (props.$noTabs === false ? '0' : `1px solid ${props.theme.borderColor}`)};
  margin-top: ${props => (props.$noTabs === false ? '-1px' : '0')};
  width: 100%;
  border-top-left-radius: ${props => (props.$noTabs === false ? '0' : '3px')};
  border-top-right-radius: ${props => (props.$noTabs === false ? '0' : '3px')};
  border-bottom-left-radius: 3px;
  border-bottom-right-radius: 3px;
  padding: 10px 10px 10px 0;
`

const CodeTabContentItemHeader = styled.div`
  height: 25px;
  align-items: center;
  margin-bottom: 10px;
  position: relative;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  button {
    border-left: 0px;
    border-top-left-radius: 0px;
    border-bottom-left-radius: 0px;
    border-color: ${props => props.theme.borderColor} !important;
    padding: 2.5px 8px;
    font-size: 12px;
    color: ${props => props.theme.textGreyTinted};
    height: 25px;
    outline: none !important;
    i {
      opacity: 0.6;
    }
    &:active,
    &:focus,
    &:hover {
      background: ${props => props.theme.bgSecondLightest} !important;
      i {
        opacity: 0.7;
      }
    }
  }
`

const PRE = styled.pre`
  margin: 0px;
  padding: 0px;
  padding-bottom: 10px;
  overflow: auto;
  border: 0px;
  background: transparent;
  font-family: monospace;
  tab-size: 2;
  direction: ltr;
  word-break: normal;
  font-family: monospace;
  white-space: pre;
  word-spacing: normal;
  text-align: left;
  hyphens: none;
`

const SelectStyles = props => ({
  container: (provided, state) => ({
    ...provided
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: '13px',
    padding: '5px 8px'
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    width: '17px',
    display: props.$readOnly ? 'none' : 'flex',
    cursor: 'pointer',
    padding: '0px'
  }),
  indicatorSeparator: provided => ({
    ...provided,
    display: props.$readOnly ? 'none' : 'inherit'
  }),
  control: (provided, state) => ({
    ...provided,
    width: '110px',
    fontSize: '13px',
    background: `${props.theme.bgLightest} !important`,
    borderColor: `${getSelectTheme(props.theme.id).borderColor} !important`,
    boxShadow: 'none !important',
    borderTopRightRadius: '0px',
    borderBottomRightRadius: '0px',
    padding: '0px 3px 0px 6px',
    marginTop: '1px'
  }),
  menu: (provided, state) => ({
    ...provided,
    boxShadow: getSelectTheme(props.theme.id).boxShadow,
    border: `1px solid ${getSelectTheme(props.theme.id).borderColor}`,
    zIndex: 102,
    fontSize: '13px'
  })
})
